import { closestCenter, DndContext, DragEndEvent, DragOverEvent, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  MenuBook,
} from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from '@tanstack/react-router';
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateTextDialog } from '@/components/editor/CreateTextDialog';
import { NarrativeSummary } from '@/lib/types';

interface OutlineDrawerProps {
  isOpen: boolean;
  isDesktopVisible?: boolean;
  expandedChapters: Record<string, boolean>;
  setExpandedChapters: Dispatch<SetStateAction<Record<string, boolean>>>;
  onClose: () => void;
  chapters: NarrativeSummary[];
  scenes: NarrativeSummary[];
  onSceneClick: (scene: NarrativeSummary) => void;
  onCreateScene?: (name: string, chapterId: string) => Promise<void>;
  onCreateChapter?: (name: string) => Promise<string | null>;
  onReorderScenes?: (scenesByChapter: Record<string, NarrativeSummary[]>) => Promise<void>;
  selectedTextId?: string | null;
  pendingSceneId?: string | null;
}

export function OutlineDrawer({
  isOpen,
  isDesktopVisible = true,
  expandedChapters,
  setExpandedChapters,
  onClose,
  chapters,
  scenes,
  onSceneClick,
  onCreateScene,
  onCreateChapter,
  onReorderScenes,
  selectedTextId,
  pendingSceneId,
}: OutlineDrawerProps) {
  const { t } = useTranslation();
  const [targetChapterId, setTargetChapterId] = useState<string | null>(null);
  const [isCreateChapterDialogOpen, setIsCreateChapterDialogOpen] = useState(false);
  const [previewScenesByChapter, setPreviewScenesByChapter] = useState<ScenesByChapter | null>(null);
  const previewScenesByChapterRef = useRef<ScenesByChapter | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // The outline is a permanent navigation sidebar on desktop. On smaller
  // screens it remains a temporary drawer so the writing area stays usable.
  const isVisible = isOpen && (isMobile || isDesktopVisible);
  const canCreateStructure = Boolean(onCreateScene && onCreateChapter);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const orderedChapters = useMemo(() => (
    [...chapters]
      .filter((chapter) => !chapter.isArchive)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.updatedAt ?? '').localeCompare(b.updatedAt ?? ''))
  ), [chapters]);
  const orderedScenes = useMemo(() => (
    [...scenes]
      .filter((scene) => !scene.isArchive)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.updatedAt ?? '').localeCompare(b.updatedAt ?? ''))
  ), [scenes]);

  useEffect(() => {
    if (!isVisible || !selectedTextId) return;

    const activeScene = scenes.find((scene) => (
      scene.id === selectedTextId ||
      scene.mainContentId === selectedTextId ||
      scene.contentVariants?.includes(selectedTextId)
    ));
    if (!activeScene) return;

      const chapterBucket = activeScene.chapterId || 'unsorted';
    setExpandedChapters((previous) => (
      previous[chapterBucket] ? previous : { ...previous, [chapterBucket]: true }
    ));
  }, [isVisible, scenes, selectedTextId]);

  const toggleChapter = (chapterId: string) => {
    setExpandedChapters((prev) => ({
      ...prev,
      [chapterId]: !prev[chapterId],
    }));
  };

  const handleCreateScene = async (name: string) => {
    if (!targetChapterId || !onCreateScene) return;
    await onCreateScene(name, targetChapterId);
  };

  const handleCreateChapter = async (name: string) => {
    if (!onCreateChapter) return;
    const chapterId = await onCreateChapter(name);
    if (chapterId) {
      setExpandedChapters((previous) => ({ ...previous, [chapterId]: true }));
    }
  };

  // Group scenes by chapter, filtering out deprecated ones
  type ChapterId = NarrativeSummary["id"];
  type ChapterBucketId = ChapterId | "unsorted";
  type ScenesByChapter = Record<ChapterBucketId, NarrativeSummary[]>;
  const scenesByChapter = orderedScenes
    .reduce((acc, scene) => {
      const chapterId: ChapterBucketId = scene.chapterId || "unsorted";
      if (!acc[chapterId]) acc[chapterId] = [];
      acc[chapterId].push(scene);
      return acc;
    }, {} as ScenesByChapter);
  const displayedScenesByChapter = previewScenesByChapter ?? scenesByChapter;

  useEffect(() => {
    setPreviewScenesByChapter(null);
    previewScenesByChapterRef.current = null;
  }, [orderedScenes]);

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) return;

    const sceneId = String(active.id).replace('scene-drag:', '');
    const overId = String(over.id);
    const targetSceneId = overId.startsWith('scene-drop:') ? overId.replace('scene-drop:', '') : undefined;
    const current = previewScenesByChapterRef.current ?? scenesByChapter;
    const next = Object.fromEntries(Object.entries(current).map(([bucket, items]) => [bucket, [...items]])) as ScenesByChapter;
    const sourceBucket = Object.keys(next).find((bucket) => next[bucket].some((scene) => scene.id === sceneId));
    if (!sourceBucket) return;

    const movedScene = next[sourceBucket].find((scene) => scene.id === sceneId);
    if (!movedScene) return;
    const targetBucket = targetSceneId
      ? Object.keys(next).find((bucket) => next[bucket].some((scene) => scene.id === targetSceneId))
      : overId.startsWith('chapter-drop:') ? overId.replace('chapter-drop:', '') : null;
    if (!targetBucket) return;

    next[sourceBucket] = next[sourceBucket].filter((scene) => scene.id !== sceneId);
    const targetItems = next[targetBucket] || [];
    const targetIndex = targetSceneId ? targetItems.findIndex((scene) => scene.id === targetSceneId) : targetItems.length;
    targetItems.splice(targetIndex < 0 ? targetItems.length : targetIndex, 0, {
      ...movedScene,
      chapterId: targetBucket === 'unsorted' ? '' : targetBucket,
    });
    next[targetBucket] = targetItems;
    previewScenesByChapterRef.current = next;
    setPreviewScenesByChapter(next);
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      onClose={onClose}
      anchor="left"
      open={isVisible}
      PaperProps={{ sx: { width: isMobile ? '90vw' : 360, overflow: 'hidden' } }}
      sx={{
        width: isVisible ? 360 : 0,
        flexShrink: 0,
        transition: theme => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: isMobile ? '90vw' : 360,
          boxSizing: 'border-box',
          position: isMobile ? undefined : 'relative',
          borderRight: 1,
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          p: 1.5,
          pl: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBook sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('editor.outline')}
            </Typography>
          </Box>
          {isMobile && (
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
              <ChevronLeft sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={(args) => {
              const pointerIntersections = pointerWithin(args);
              const sceneIntersection = pointerIntersections.find(({ id }) => String(id).startsWith('scene-drop:'));
              return sceneIntersection ? [sceneIntersection] : pointerIntersections.length ? pointerIntersections : closestCenter(args);
            }}
            onDragOver={handleDragOver}
            onDragCancel={() => {
              setPreviewScenesByChapter(null);
              previewScenesByChapterRef.current = null;
            }}
            onDragEnd={async ({ over }: DragEndEvent) => {
              const preview = previewScenesByChapterRef.current;
              setPreviewScenesByChapter(null);
              previewScenesByChapterRef.current = null;
              if (!over || !preview) return;
              await onReorderScenes?.(preview);
            }}
          >
            <List dense>
            {orderedChapters.map((chapter) => (
              <ChapterDropZone key={chapter.id} chapterId={chapter.id}>
                <ListItemButton onClick={() => toggleChapter(chapter.id)} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {expandedChapters[chapter.id] ? <ExpandMore /> : <ChevronRight />}
                  </ListItemIcon>
                  <ListItemText
                    primary={chapter.name || t('editor.untitled_chapter')}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  />
                  {chapter.mainContentId && (
                    <Tooltip title={t('editor.open_chapter_editor')}>
                      <IconButton
                        component={Link}
                        to={`/publish-editor/${chapter.id}`}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          p: 0.5,
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <MenuBook sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canCreateStructure && <Tooltip title={t('editor.create_new_scene')}>
                    <IconButton
                      aria-label={t('editor.create_new_scene')}
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setTargetChapterId(chapter.id);
                      }}
                      sx={{
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      <Add sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>}
                </ListItemButton>
                <Collapse in={expandedChapters[chapter.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {(displayedScenesByChapter[chapter.id] || []).map((scene) => (
                      <DraggableSceneRow
                        key={scene.id}
                        scene={scene}
                        selectedTextId={selectedTextId}
                        pendingSceneId={pendingSceneId}
                        untitledSceneText={t('editor.untitled_scene')}
                        draggable={Boolean(onReorderScenes)}
                        onClick={() => onSceneClick(scene)}
                      />
                    ))}
                    {(!displayedScenesByChapter[chapter.id] || displayedScenesByChapter[chapter.id].length === 0) && (
                      <Typography key="no-scenes" variant="caption" sx={{ pl: 6, py: 1, color: 'text.disabled', display: 'block' }}>
                        {t('editor.no_scenes')}
                      </Typography>
                    )}
                  </List>
                </Collapse>
              </ChapterDropZone>
            ))}

            {/* Unsorted scenes */}
            {displayedScenesByChapter['unsorted'] && displayedScenesByChapter['unsorted'].length > 0 && (
              <ChapterDropZone key="unsorted-section" chapterId="unsorted">
                <ListItemButton onClick={() => toggleChapter('unsorted')} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {expandedChapters['unsorted'] ? <ExpandMore /> : <ChevronRight />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('editor.unsorted')}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  />
                </ListItemButton>
                <Collapse in={expandedChapters['unsorted']} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {displayedScenesByChapter['unsorted'].map((scene) => (
                      <DraggableSceneRow
                        key={scene.id}
                        scene={scene}
                        selectedTextId={selectedTextId}
                        pendingSceneId={pendingSceneId}
                        untitledSceneText={t('editor.untitled_scene')}
                        draggable={Boolean(onReorderScenes)}
                        onClick={() => onSceneClick(scene)}
                      />
                    ))}
                  </List>
                </Collapse>
              </ChapterDropZone>
            )}
            </List>
          </DndContext>
        </Box>
        {canCreateStructure && <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setIsCreateChapterDialogOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('editor.create_new_chapter')}
          </Button>
        </Box>}
        {canCreateStructure && <CreateTextDialog
          open={Boolean(targetChapterId)}
          onClose={() => setTargetChapterId(null)}
          onCreate={handleCreateScene}
        />}
        {canCreateStructure && <CreateTextDialog
          open={isCreateChapterDialogOpen}
          dialogTitle={t('editor.create_new_chapter')}
          onClose={() => setIsCreateChapterDialogOpen(false)}
          onCreate={handleCreateChapter}
        />}
      </Box>
    </Drawer>
  );
}

function ChapterDropZone({ chapterId, children }: { chapterId: string; children: ReactNode }) {
  const { setNodeRef } = useDroppable({ id: `chapter-drop:${chapterId}` });

  return (
    <Box ref={setNodeRef}>
      {children}
    </Box>
  );
}

function DraggableSceneRow({
  scene,
  selectedTextId,
  pendingSceneId,
  untitledSceneText,
  draggable,
  onClick,
}: {
  scene: NarrativeSummary;
  selectedTextId?: string | null;
  pendingSceneId?: string | null;
  untitledSceneText: string;
  draggable: boolean;
  onClick: () => void;
}) {
  const { attributes, isDragging, listeners, setNodeRef: setDragRef, transform } = useDraggable({
    id: `scene-drag:${scene.id}`,
    disabled: !draggable,
  });
  const { setNodeRef: setDropRef } = useDroppable({ id: `scene-drop:${scene.id}` });
  const isSelected = selectedTextId === scene.mainContentId || selectedTextId === scene.id || scene.contentVariants?.includes(selectedTextId || '');

  return (
    <ListItemButton
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      onClick={onClick}
      {...attributes}
      {...listeners}
      sx={{
        pl: 6,
        borderRadius: 1,
        mb: 0.2,
        cursor: draggable ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        opacity: isDragging ? 0.7 : 1,
        transform: CSS.Translate.toString(transform),
        transition: 'opacity 150ms ease',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <ListItemText
        primary={scene.name || untitledSceneText}
        primaryTypographyProps={{
          variant: 'body2',
          color: isSelected ? 'primary' : 'text.primary',
          fontWeight: isSelected ? 600 : 400,
        }}
      />
      {pendingSceneId === scene.id && <CircularProgress size={16} sx={{ ml: 1 }} color="inherit" />}
    </ListItemButton>
  );
}
